import Phaser from 'phaser';
import type { WarningRingState } from '../entities/boss';

/**
 * Draws a countdown charge ring on a Phaser.GameObjects.Graphics object.
 *
 * The ring sweeps clockwise from the top (12 o'clock) as progress goes 0 → 1.
 * At progress 0 the ring is invisible (alpha 0).
 * At progress 1 the ring forms a full circle (360°).
 *
 * @param gfx      Phaser Graphics object to draw on (cleared before drawing).
 * @param state    WarningRingState snapshot from Boss.getWarningRingDescriptor().
 * @param radiusPx Radius of the ring in pixels (default 28).
 * @param thickness Line thickness in pixels (default 4).
 */
export function drawChargeRing(
  gfx: Phaser.GameObjects.Graphics,
  state: WarningRingState,
  radiusPx = 28,
  thickness = 4,
): void {
  gfx.clear();

  if (!state.active || state.progress <= 0) return;

  const { x, y, color, progress } = state;

  // Outer dim background ring (always full circle while active).
  gfx.lineStyle(thickness, color, 0.25);
  gfx.strokeCircle(x, y, radiusPx);

  // Filled arc sweeping clockwise from top.
  // Phaser arc: startAngle/endAngle in degrees, measured from 3 o'clock CW.
  // Top (12 o'clock) = -90°.
  const startDeg = -90;
  const endDeg = startDeg + progress * 360;

  gfx.lineStyle(thickness, color, 0.9 + 0.1 * progress);
  gfx.beginPath();
  // strokePoints draws nothing useful for arcs — use Phaser arc helper.
  // Arc API: (x, y, radius, startAngle, endAngle, anticlockwise)
  // All angles in degrees for Phaser.GameObjects.Graphics.arc.
  gfx.arc(x, y, radiusPx, Phaser.Math.DegToRad(startDeg), Phaser.Math.DegToRad(endDeg), false);
  gfx.strokePath();

  // Pulse: small filled circle at the arc tip to make progress legible.
  const tipAngle = Phaser.Math.DegToRad(endDeg);
  const tipX = x + Math.cos(tipAngle) * radiusPx;
  const tipY = y + Math.sin(tipAngle) * radiusPx;
  gfx.fillStyle(color, 1);
  gfx.fillCircle(tipX, tipY, thickness);
}
